/**
 * Stripe service for handling subscription checkout
 * This service provides functions to interact with the Stripe Checkout Cloud Function
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Creates a Stripe Checkout session for the specified plan
 * @param {string} plan - The subscription plan ('pro' or 'growth')
 * @returns {Promise<{sessionId: string, url: string}>} Checkout session details
 * @throws {Error} If the function call fails
 */
export async function createCheckoutSession(plan) {
  if (!plan || (plan !== 'pro' && plan !== 'growth')) {
    throw new Error('Plan must be either "pro" or "growth"');
  }

  try {
    const functions = getFunctions();
    const createCheckoutSessionFunction = httpsCallable(functions, 'createCheckoutSession');
    
    const result = await createCheckoutSessionFunction({ plan });
    
    return {
      sessionId: result.data.sessionId,
      url: result.data.url,
    };
  } catch (error) {
    console.error('Stripe checkout error:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Error details:', error.details);
    
    // Handle Firebase function errors
    if (error.code === 'functions/unauthenticated') {
      throw new Error('You must be logged in to create a checkout session');
    } else if (error.code === 'functions/invalid-argument') {
      throw new Error(error.message || 'Invalid plan specified');
    } else if (error.code === 'functions/already-exists') {
      throw new Error(error.message || 'You are already on this plan');
    } else if (error.code === 'functions/failed-precondition') {
      throw new Error(error.message || 'Stripe is not configured properly');
    } else if (error.code === 'functions/internal') {
      // Show the actual error message from the function
      const errorMessage = error.message || error.details || 'An internal error occurred';
      throw new Error(errorMessage);
    } else {
      throw new Error(error.message || 'Failed to create checkout session');
    }
  }
}

/**
 * Redirects user to Stripe Checkout
 * @param {string} plan - The subscription plan ('pro' or 'growth')
 */
export async function redirectToCheckout(plan) {
  try {
    const { url } = await createCheckoutSession(plan);
    window.location.href = url;
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
}
